import React, { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { FaPrint } from "react-icons/fa6";

// Design Components
import Container from "../../atoms/Container/Container";
import Print from "../Print/Print";
import Button from "../../atoms/Button/Button";
import { ButtonSizes, ButtonTypes } from "../../atoms/Button/Button.types";
import Icon from "../../atoms/Icon/Icon";
import Link from "../../atoms/Link/Link";
import { IconValues } from "../../atoms/Icon/Icon.types";
import Logo from "../../molecules/Logo/Logo";
import Bar from "../../charts/Bar/Bar";

// NWEAReport Component Stuff
import { getSubject, getPercentileCategory } from "./NWEAReport.helpers";
import {
  StudentData,
  OverallData,
  Skill,
  Subscore,
  NWEAReportProps,
} from "./NWEAReport.types";
import * as NWEAConstants from "./NWEAReport.constants";

import "./NWEAReport.scss";
import "../../tokens/alignment.scss";

const NWEAReport: React.FC<NWEAReportProps> = ({ report }) => {
  const student = report.student;
  const overall = report.overall;
  const showAttention = report.attentionFlag;
  const skills = report.skills;
  const subscores = report.subscores;

  const componentRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  return (
    <div className="mp-templates-nweareport--main">
      {report && (
        <>
          <Container>
            <header className="nwea-header-container">
              <section className="nwea-print-button">
                <Button
                  primary={true}
                  label={
                    <span className="icon-text">
                      <FaPrint />
                      <p>Print</p>
                    </span>
                  }
                  size={ButtonSizes.icon}
                  type={ButtonTypes.neutral}
                  onClick={handlePrint}
                />
              </section>

              <section className="nwea-header">
                <StudentHeader student={student} />
                <SubjectHeader subject={student.subject} />
              </section>
            </header>
            <Overall overallData={overall} />
            <Projected
              showAttention={showAttention}
              skills={skills}
              mpTestDate={student.mpTestDate}
              mpPercentile={overall.mpPercentile}
              height={NWEAConstants.WEB_CHART_HEIGHT}
              subid={student.subid}
              firstName={student.first_name}
              webVersion={true}
              subject={student.subject}
            />
            <SubscoreCard subscores={subscores} />
          </Container>

          <Print ref={componentRef}>
            <div className="mp-templates-nweareport--main-print">
              <div className="page">
                <header className="print-header-container">
                  <section className="print-logo-student">
                    <div className="print-mp-logo">
                      <Logo responsive={true} />
                    </div>
                    <StudentHeader student={student} />
                  </section>
                  <section className="nwea-subject-header">
                    <SubjectHeader subject={student.subject} />
                  </section>
                </header>
                <Overall overallData={overall} />
                <Projected
                  showAttention={showAttention}
                  skills={skills}
                  mpTestDate={student.mpTestDate}
                  mpPercentile={overall.mpPercentile}
                  height={NWEAConstants.PRINT_CHART_HEIGHT}
                  subid={student.subid}
                  firstName={student.first_name}
                  webVersion={false}
                  subject={student.subject}
                />
              </div>
              <div className="page">
                <header className="nwea-header-page2">
                  <div className="nwea-student-header">
                    <h2>{`${student.first_name} ${student.last_name}`}</h2>
                  </div>
                  <div className="nwea-learning-plan2">
                    <h3>Learning Plan</h3>
                    <h4>{getSubject(student.subject)}</h4>
                  </div>
                </header>
                <SubscoreCard subscores={subscores} />
                <footer className="nwea-footer-page2">
                  <div className="print-mp-logo">
                    <Logo responsive={true} />
                  </div>
                  <div className="nwea-footer-map-growth">
                    <Icon value={IconValues.mapGrowth} />
                  </div>
                </footer>
              </div>
            </div>
          </Print>
        </>
      )}
    </div>
  );
};

export default NWEAReport;

const StudentHeader: React.FC<{
  student: StudentData;
}> = ({ student }) => {
  return (
    <div className="nwea-student-header">
      <h2>{`${student.first_name} ${student.last_name}`}</h2>
      <div className="nwea-student-info">
        <h5>Grade:&nbsp;</h5>
        <p>{student.grade}</p>
      </div>
      <div className="nwea-student-info">
        <h5>School:&nbsp;</h5>
        <p>{student.school}</p>
      </div>
      <div className="nwea-student-info">
        <h5>MAP Test Date:&nbsp;</h5>
        <p>{student.termName}</p>
      </div>
    </div>
  );
};

const SubjectHeader: React.FC<{
  subject: string;
}> = ({ subject }) => {
  return (
    <div className="nwea-subject-header">
      <div className="nwea-header-map-growth">
        <Icon value={IconValues.mapGrowth} />
      </div>
      <div className="nwea-learning-plan">
        <h3>Learning Plan</h3>
        <h4>{getSubject(subject)}</h4>
      </div>
    </div>
  );
};

const Overall: React.FC<{
  overallData: OverallData;
}> = ({ overallData }) => {
  return (
    <div className="nwea-overall-container">
      <header className="nwea-overall-header">
        <h2>{overallData.name}</h2>
      </header>
      <section className="nwea-overall-score">
        <div className="nwea-performance">
          <h5>Actual vs. Projected Performance</h5>
          <div className="nwea-score-container">
            <div className="nwea-overall-rit">
              <Icon value={IconValues.map} />
              <p>RIT Score:&nbsp;</p>
            </div>
            <div
              className="nwea-pill"
              style={{
                backgroundColor: getPercentileCategory(
                  overallData.ritPercentile
                ).color,
                color: getPercentileCategory(overallData.ritPercentile)
                  .textColor,
              }}
            >
              {overallData.rit}
            </div>
          </div>
          <div className="nwea-score-container">
            <div className="nwea-overall-projected">
              <Icon value={IconValues.logo} />
              <p>Projected Score:&nbsp;</p>
            </div>
            <div
              className="nwea-pill"
              style={{
                backgroundColor: getPercentileCategory(overallData.mpPercentile)
                  .color,
                color: getPercentileCategory(overallData.mpPercentile)
                  .textColor,
              }}
            >
              {getPercentileCategory(overallData.mpPercentile).text}
            </div>
          </div>
        </div>
        <div className="nwea-map-scale-container">
          <ProjectedScoreBar
            mpPercentile={overallData.mpPercentile}
            ritPercentile={overallData.ritPercentile}
            subScoreCard={false}
          />
        </div>
      </section>
    </div>
  );
};

const ProjectedScoreBar: React.FC<{
  mpPercentile: number;
  ritPercentile: number;
  subScoreCard: boolean;
}> = ({ mpPercentile, ritPercentile, subScoreCard }) => {
  const rp = ritPercentile * 100;
  const mp = mpPercentile * 100;
  const mpLeftArrow = mpPercentile * 100 + 1.7;
  const diff = Math.abs(mp - rp);

  const makeRitScoreAgainstPotential = () => {
    if (mp >= rp - 15 && mp <= rp + 15) {
      return "consistent";
    } else if (mp < rp - 15) {
      return "monitor";
    } else {
      return "opportunity";
    }
  };
  const ritScoreAgainstPotential = makeRitScoreAgainstPotential();

  return (
    <div className="nwea-scale-container">
      <div className="nwea-caret-section" style={{ left: rp + "%" }}>
        <div className="nwea-map-logo">
          <Icon value={IconValues.map} />
        </div>
        <div className="nwea-caret">
          <Icon value={IconValues.caretDown} />
        </div>
      </div>

      <div className="nwea-scale-section">
        <div className="nwea-scale-quinary nwea-low-bg" />
        <div className="nwea-scale-quinary nwea-low-avg-bg" />
        <div className="nwea-scale-quinary nwea-mid-bg" />
        <div className="nwea-scale-quinary nwea-high-avg-bg" />
        <div className="nwea-scale-quinary nwea-high-bg" />
      </div>

      {/* Verticle Lines and Arrow */}
      {mp > rp ? (
        <div className="nwea-arrow-section">
          {/* ritPercentile line */}
          <div className="nwea-verticle-line" style={{ left: rp + "%" }} />
          <div
            className="nwea-arrow-line"
            style={{ left: rp + "%", width: diff + "%" }}
          />
          {ritScoreAgainstPotential !== "consistent" ? (
            <div className="nwea-right-arrow" style={{ left: rp + "%" }}>
              <Icon value={IconValues.chevronRight} />
            </div>
          ) : (
            <></>
          )}
          {/* mpPercentile line */}
          <div className="nwea-verticle-line" style={{ left: rp + "%" }} />
        </div>
      ) : (
        <div className="nwea-arrow-section">
          {/* mpPercentile line */}
          <div className="nwea-verticle-line" style={{ left: mp + "%" }} />
          {ritScoreAgainstPotential !== "consistent" ? (
            <div
              className="nwea-left-arrow"
              style={
                subScoreCard
                  ? { left: mpLeftArrow + 0.7 + "%" }
                  : { left: mpLeftArrow + "%" }
              }
            >
              <Icon value={IconValues.chevronLeft} />
            </div>
          ) : (
            <></>
          )}
          <div
            className="nwea-arrow-line"
            style={{ left: mp + "%", width: diff + "%" }}
          />
          {/* ritPercentile line */}
          <div className="nwea-verticle-line" style={{ left: mp + "%" }} />
        </div>
      )}

      {/* Status Text and MP Brain Icon */}
      {mp < 50 ? (
        <div className="nwea-status-section">
          <div className="nwea-scale-mp-logo" style={{ left: mp - 2 + "%" }}>
            <Icon value={IconValues.logo} />
          </div>
          <h5 className="nwea-scale-status-text" style={{ left: mp + "%" }}>
            {ritScoreAgainstPotential}
          </h5>
        </div>
      ) : (
        <div
          className="nwea-status-section"
          style={subScoreCard ? { position: "relative", right: 9 + "%" } : {}}
        >
          <h5
            className="nwea-scale-status-text"
            style={{ left: mp - 21 + "%" }}
          >
            {ritScoreAgainstPotential}
          </h5>

          {/* Adjust for text length */}
          {ritScoreAgainstPotential === "consistent" ? (
            <div className="nwea-scale-mp-logo" style={{ left: mp - 18 + "%" }}>
              <Icon value={IconValues.logo} />
            </div>
          ) : (
            <div className="nwea-scale-mp-logo" style={{ left: mp - 20 + "%" }}>
              <Icon value={IconValues.logo} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Projected: React.FC<{
  showAttention: boolean;
  skills: Skill[];
  mpTestDate: string;
  mpPercentile: number;
  height: number;
  subid: string | undefined;
  firstName: string;
  webVersion: boolean;
  subject: string;
}> = ({
  showAttention,
  skills,
  mpTestDate,
  mpPercentile,
  height,
  subid,
  firstName,
  webVersion,
  subject,
}) => {
  // create values array for Bar component
  const values = skills.map((skill) => ({
    value: skill.value > 0.05 ? skill.value : 0.05,
    bgColor: skill.validity ? "#f27277" : "#909090",
  }));

  const calcMpPercentile = mpPercentile > 0.05 ? mpPercentile : 0.05;
  const titles = skills.map((skill) => skill.title);
  // add nwea-projected-line-wrapper height so line is centered
  const projectedLine = height * calcMpPercentile + 26;
  const invalidScores = skills
    .filter((skill) => !skill.validity)
    .map((skill) => skill.title);
  const makeInvalidMessage = () => {
    let errorMessage;
    let title;

    if (invalidScores.length > 2) {
      title = "Invalid Scores:";
      errorMessage = `${firstName} had invalid scores on`;
      const lastSkill = invalidScores.pop();
      errorMessage += ` ${invalidScores.join(", ")}, and ${lastSkill}.`;
    } else if (invalidScores.length == 2) {
      title = "Invalid Scores:";
      errorMessage = `${firstName} had invalid scores on`;
      const lastSkill = invalidScores.pop();
      errorMessage += ` ${invalidScores.join(", ")} and ${lastSkill}.`;
    } else {
      title = "Invalid Score:";
      errorMessage = `${firstName} had an invalid score on`;
      errorMessage += ` ${invalidScores[0]}.`;
    }
    errorMessage += ` Invalid scores are treated as if the student had difficulty with the skill. You can read more about invalid scores in ${firstName}'s Mindprint Profile to determine if ${firstName}'s performance might not accurately reflect their capabilities.`;
    return (
      <p>
        <b>{title}&nbsp;</b>
        {errorMessage}
      </p>
    );
  };

  return (
    <div className="nwea-projected-container">
      <h3>
        What skills drive your MindPrint Projected {subject} Score?
        {showAttention && " †"}
      </h3>
      <p>
        The MindPrint Projected Score is a weighted calculation that identifies
        the student's expected performance based on the MindPrint assessment of
        cognitive skills. Weightings were developed through statistical
        correlation across cognitive and academic skills for that grade level.
        Weightings are shown in order of significance for the top four cognitive
        skills for {subject}.
      </p>
      <div className="nwea-bar-wrapper">
        <Bar values={values} min={0} max={1} height={height} />

        <section
          className="nwea-projected-line-wrapper"
          style={{ bottom: projectedLine + "px" }}
        >
          <Icon value={IconValues.logo} />
          <div
            className="nwea-projected-line"
            style={{
              backgroundColor: getPercentileCategory(mpPercentile).color,
            }}
          />
          <div
            className="nwea-pill"
            style={{
              backgroundColor: getPercentileCategory(mpPercentile).color,
              color: getPercentileCategory(mpPercentile).textColor,
            }}
          >
            {getPercentileCategory(mpPercentile).text}
          </div>
        </section>

        <div className="nwea-base-line" />

        <section className="nwea-bar-titles">
          {titles.map((title, index) => {
            return (
              <p key={index} className="bar-section-title">
                {index + 1}. {title}
              </p>
            );
          })}
        </section>
      </div>

      {webVersion && (
        <div className="nwea-view-mp">
          <a
            href={`https://admin.v2.mindprintlearning.com/mindprint-profile/${subid}`}
            target="_blank"
          >
            View MindPrint profile
          </a>
          <p>({new Date(mpTestDate).toLocaleDateString()})</p>
        </div>
      )}

      <p>{NWEAConstants.P2}</p>
      {invalidScores && invalidScores.length > 0 && makeInvalidMessage()}

      {showAttention && (
        <div className="nwea-attention-wrapper">
          <p>
            <b className="nwea-attention-header">† Attention: </b>Students with
            weaker&nbsp;
            <a
              href="https://my.mindprintlearning.com/product/11074"
              target="_blank"
            >
              Attention&nbsp;
            </a>
            like {firstName} can be prone to variability in performance. You
            might see larger discrepancies between MindPrint projected
            performance and actual RIT scores for some subjects or greater than
            expected variance in RIT growth. These strategies can help{" "}
            {firstName} during MAP testing so performance reflects full
            capabilities:
          </p>
          <div className="nwea-attention-strategies">
            <p>
              1:&nbsp;
              <a
                href="https://my.mindprintlearning.com/product/12838"
                target="_blank"
              >
                Online Test Taking Strategies
              </a>
            </p>
            <p>
              2:&nbsp;
              <a
                href="https://my.mindprintlearning.com/product/12393"
                target="_blank"
              >
                Sustain Focus on Standardized Tests
              </a>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const SubscoreCard: React.FC<{
  subscores: Subscore[];
}> = ({ subscores }) => {
  return (
    <div className="nwea-subscore-card-container">
      {subscores.map((subskill, index) => {
        return (
          <div key={index} className="nwea-subscore-card-wrapper">
            <h2>{subskill.name}</h2>
            <header className="nwea-subscore-header">
              <div className="nwea-subscore-rit">
                <div className="nwea-subscore-rit-score">
                  <Icon value={IconValues.map} />
                  <p>RIT Score:&nbsp;</p>
                </div>
                <div
                  className="nwea-pill"
                  style={{
                    backgroundColor: getPercentileCategory(
                      subskill.ritPercentile
                    ).color,
                    color: getPercentileCategory(subskill.ritPercentile)
                      .textColor,
                  }}
                >
                  {subskill.rit}
                </div>
              </div>

              <div className="nwea-subscore-rit">
                <div className="nwea-subscore-projected-score">
                  <Icon value={IconValues.logo} />
                  <p>Projected Score:&nbsp;</p>
                </div>
                <div
                  className="nwea-pill"
                  style={{
                    backgroundColor: getPercentileCategory(
                      subskill.mpPercentile
                    ).color,
                    color: getPercentileCategory(subskill.mpPercentile)
                      .textColor,
                  }}
                >
                  {getPercentileCategory(subskill.mpPercentile).text}
                </div>
              </div>
            </header>

            <section className="nwea-cat-scale-wrapper">
              <ProjectedScoreBar
                mpPercentile={subskill.mpPercentile}
                ritPercentile={subskill.ritPercentile}
                subScoreCard={true}
              />
            </section>

            <main className="nwea-subscore-strategies">
              <div className="nwea-ts-list-wrapper">
                <h3>Teaching Strategies</h3>
                {subskill.teachingStrategies.map((teachingStrategy, index) => {
                  return (
                    <div key={index} className="nwea-ts-list">
                      <Link to={teachingStrategy.url} target="_blank">
                        {teachingStrategy.text}
                      </Link>
                    </div>
                  );
                })}
              </div>
              <a
                href={subskill.moreStrategies.url}
                className="nwea-more-strategies"
                target="_blank"
              >
                {subskill.moreStrategies.text}
              </a>
            </main>
          </div>
        );
      })}
    </div>
  );
};
